import { ThemeOptions } from '@mui/material/styles';
import { logoThemeLight, logoThemeDark } from '../logoConfig';

const baseTheme: Partial<ThemeOptions> = {
  palette: {
    primary: {
      main: '#5C6F7B',
      light: '#8A9BA8',
      dark: '#3A4750',
    },
    secondary: {
      main: '#7A8B7F',
      light: '#A1B5A7',
      dark: '#546159',
    },
    success: {
      main: '#6B8E71',
      light: '#8FAE95',
      dark: '#4C6550',
    },
    warning: {
      main: '#C19660',
      light: '#D4B088',
      dark: '#9A7343',
    },
    error: {
      main: '#A66B6B',
      light: '#C19090',
      dark: '#7D4A4A',
    },
    info: {
      main: '#6B8FA6',
      light: '#90B0C4',
      dark: '#4A6780',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.4,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
    },
  },
  shape: {
    borderRadius: 8,
  },
};

export const industrialSlateLight: ThemeOptions = {
  ...baseTheme,
  logo: logoThemeLight,
  palette: {
    ...baseTheme.palette,
    mode: 'light',
    background: {
      default: '#F5F5F3',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#3C3C3B',
      secondary: '#6B6B6A',
      disabled: '#A8A8A7',
    },
    divider: 'rgba(60, 60, 59, 0.12)',
    action: {
      active: '#3C3C3B',
      hover: 'rgba(92, 111, 123, 0.04)',
      selected: 'rgba(92, 111, 123, 0.08)',
      disabled: '#A8A8A7',
      disabledBackground: 'rgba(168, 168, 167, 0.12)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
};

export const industrialSlateDark: ThemeOptions = {
  ...baseTheme,
  logo: logoThemeDark,
  palette: {
    ...baseTheme.palette,
    mode: 'dark',
    primary: {
      main: '#7A8D9A',
      light: '#9AADB9',
      dark: '#5A6D7A',
    },
    secondary: {
      main: '#8FA094',
      light: '#A9B9AE',
      dark: '#6F8074',
    },
    background: {
      default: '#1E2022',
      paper: '#272B2E',
    },
    text: {
      primary: '#E8E8E6',
      secondary: '#B8B8B6',
      disabled: '#7A7A78',
    },
    divider: 'rgba(232, 232, 230, 0.12)',
    action: {
      active: '#E8E8E6',
      hover: 'rgba(122, 141, 154, 0.08)',
      selected: 'rgba(122, 141, 154, 0.16)',
      disabled: '#7A7A78',
      disabledBackground: 'rgba(122, 122, 120, 0.12)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.3)',
        },
      },
    },
  },
};